// Argus — useNavigationHistory: hook custom pentru File Explorer back/forward/up.
// State: currentPath + historyBack stack + historyForward stack + selection/scrollY snapshot per entry.

import { useCallback, useState } from 'react';
import { parentOfPath } from '../../lib/fsEntries';

export interface HistoryEntry {
  path: string;
  selectedItem: string | null;
  scrollY: number;
}

export interface NavigationHistory {
  currentPath: string;
  selectedItem: string | null;
  scrollY: number;
  canGoBack: boolean;
  canGoForward: boolean;
  canGoUp: boolean;
  navigate: (path: string, selectedForCurrent?: string | null) => void;
  back: () => void;
  forward: () => void;
  up: () => void;
  setSelectedItem: (name: string | null) => void;
  setScrollY: (y: number) => void;
}

export function useNavigationHistory(initialPath: string): NavigationHistory {
  const [historyBack, setHistoryBack] = useState<HistoryEntry[]>([]);
  const [historyForward, setHistoryForward] = useState<HistoryEntry[]>([]);
  const [currentPath, setCurrentPath] = useState<string>(typeof initialPath === 'string' ? initialPath : '');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState<number>(0);

  const navigate = useCallback((path: string, selectedForCurrent: string | null = null) => {
    if (typeof path !== 'string') return;
    if (path === currentPath) return;
    const snapshotSelected = selectedForCurrent !== null ? selectedForCurrent : selectedItem;
    setHistoryBack((h) => [...h, { path: currentPath, selectedItem: snapshotSelected, scrollY }]);
    setHistoryForward([]);
    setCurrentPath(path);
    setSelectedItem(null);
    setScrollY(0);
  }, [currentPath, selectedItem, scrollY]);

  const back = useCallback(() => {
    setHistoryBack((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1]!;
      setHistoryForward((f) => [...f, { path: currentPath, selectedItem, scrollY }]);
      setCurrentPath(prev.path);
      setSelectedItem(prev.selectedItem);
      setScrollY(prev.scrollY);
      return h.slice(0, -1);
    });
  }, [currentPath, selectedItem, scrollY]);

  const forward = useCallback(() => {
    setHistoryForward((f) => {
      if (f.length === 0) return f;
      const next = f[f.length - 1]!;
      setHistoryBack((h) => [...h, { path: currentPath, selectedItem, scrollY }]);
      setCurrentPath(next.path);
      setSelectedItem(next.selectedItem);
      setScrollY(next.scrollY);
      return f.slice(0, -1);
    });
  }, [currentPath, selectedItem, scrollY]);

  const up = useCallback(() => {
    const parent = parentOfPath(currentPath);
    if (parent === currentPath) return;
    navigate(parent);
  }, [currentPath, navigate]);

  const canGoUp = parentOfPath(currentPath) !== currentPath;

  return {
    currentPath,
    selectedItem,
    scrollY,
    canGoBack: historyBack.length > 0,
    canGoForward: historyForward.length > 0,
    canGoUp,
    navigate,
    back,
    forward,
    up,
    setSelectedItem,
    setScrollY,
  };
}
